import { Injectable, inject } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';

@Injectable({ providedIn: 'root' })
export class StatusBarService {
  private readonly platform = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  private readonly BRAND_COLOR = '#000000';

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      await this.setDefaultStyle();
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to initialize');
    }
  }

  private async setDefaultStyle(): Promise<void> {
    try {
      await StatusBar.setStyle({ style: Style.Dark });

      if (this.platform.isAndroid()) {
        await StatusBar.setBackgroundColor({ color: this.BRAND_COLOR });
      }

      if (this.platform.isIOS()) {
        await StatusBar.setOverlaysWebView({ overlay: true });
      }
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set default style');
    }
  }

  async setStyleLight(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set light style');
    }
  }

  async setStyleDark(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.setStyle({ style: Style.Light });
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set dark style');
    }
  }

  async setStyleDefault(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.setStyle({ style: Style.Default });
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set default style');
    }
  }

  async setBackgroundColor(color: string): Promise<void> {
    if (!this.platform.isAndroid()) return;

    try {
      await StatusBar.setBackgroundColor({ color });
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set background color');
    }
  }

  async hide(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.hide();
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to hide status bar');
    }
  }

  async show(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.show();
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to show status bar');
    }
  }

  async setOverlaysWebView(overlay: boolean): Promise<void> {
    if (!this.platform.isIOS()) return;

    try {
      await StatusBar.setOverlaysWebView({ overlay });
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to set overlay mode');
    }
  }

  async applyBrandStyle(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.setStyle({ style: Style.Dark });
      if (this.platform.isAndroid()) {
        await StatusBar.setBackgroundColor({ color: this.BRAND_COLOR });
      }
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to apply brand style');
    }
  }

  async applyLightBackgroundStyle(): Promise<void> {
    if (!this.platform.isNativeMobile()) return;

    try {
      await StatusBar.setStyle({ style: Style.Light });
      if (this.platform.isAndroid()) {
        await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
      }
    } catch (error) {
      this.loggingService.logException(error, 'StatusBarService: Failed to apply light background style');
    }
  }
}
